// otpCells.ts — the pure string maths behind the segmented sign-in-code input,
// kept out of the component so it can be tested directly. No React.
//
// The code is modelled as ONE contiguous digit string; the cells are just a
// view of it. That rules out holes ("12__5678"), which would make a length
// check meaningless — a complete code has to mean OTP_LENGTH real digits, not
// OTP_LENGTH occupied positions.
//
// EVERY rule here is parameterised on OTP_LENGTH: there is no literal digit
// count in this file, no `\d{n}` regex and no fixed slice. That is deliberate —
// a stray hard-coded length would silently reject every real code, which is the
// exact failure mode the shared constant exists to prevent.
//
// Ported from the fedex booking portal's `auth/otp-cells.ts`, which learned all
// of this the hard way — including its length, which is 8 there too.

import { LOGIN_CODE_LENGTH } from '@l2u/shared/loginCode';

/**
 * Digits in an emailed code.
 *
 * NOT this repo's decision to make: GoTrue generates the code and its length is
 * a Supabase dashboard setting. 8 is what the projects already emit, which is
 * why this feature needs no dashboard change — see `@l2u/shared/loginCode`.
 */
export const OTP_LENGTH = LOGIN_CODE_LENGTH;

/** Digits only, capped at the code length. Every entry point funnels through this. */
export function sanitize(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, OTP_LENGTH);
}

/** A full code, ready to submit. */
export function isComplete(code: string): boolean {
  return code.length === OTP_LENGTH;
}

/**
 * Writes `v` at cell `i`. Typing beyond the current length appends rather than
 * leaving a gap, so `i` is clamped to the end of the code.
 *
 * Returns the next code, the cell that should take focus (null = leave focus
 * put), and whether the result is now complete — the design submits as the last
 * cell fills.
 */
export function writeCell(
  code: string,
  i: number,
  v: string,
): { code: string; focus: number | null; submit: boolean } {
  const at = Math.min(i, code.length);
  const next = sanitize(code.slice(0, at) + v + code.slice(at + 1));
  return {
    code: next,
    focus: v ? Math.min(at + 1, OTP_LENGTH - 1) : null,
    submit: !!v && isComplete(next),
  };
}

/**
 * Backspace in an EMPTY cell: removes the digit before it and focuses that
 * position. (Backspace in a filled cell is left to the browser, which clears it
 * via a normal edit.)
 */
export function deleteBefore(code: string, i: number): { code: string; focus: number } {
  const prev = Math.max(0, Math.min(i, code.length) - 1);
  return { code: code.slice(0, prev) + code.slice(prev + 1), focus: prev };
}

/**
 * A whole code arriving at once — a paste, or a multi-character insert from
 * mobile one-time-code autofill (which ignores maxLength and drops the full
 * code into a single cell). `submit` is true only for a complete code; a short
 * paste fills the cells and waits.
 */
export function fillAll(raw: string): { code: string; focus: number; submit: boolean } | null {
  const code = sanitize(raw);
  if (!code) return null;
  return { code, focus: Math.min(code.length, OTP_LENGTH - 1), submit: isComplete(code) };
}

// ----------------------------------------------------------------------
// Refusals, and why they get their own two functions
//
// Auto-submitting on the last cell is the right behaviour for a code that
// works. It is disastrous for one that does not. Leave a full code in the cells
// after a refusal and the input is still "complete", so the very next
// keystroke fires another POST — and because a cell selects its contents on
// focus, correcting the code means retyping over it, which is up to
// OTP_LENGTH submissions, all but the last of them certain to fail.
//
// That is not merely wasteful. Codes are verified SERVER-side, so every user
// of a workspace shares the Worker's egress IP, and GoTrue rate-limits
// /auth/v1/verify per IP (360/hour, bursts of 30). One person fumbling one
// digit therefore spends several slots out of a budget everybody else in that
// workspace draws from.
//
// The cells ARE disabled while a verify request is in flight — that is
// `disabled={verifying}` in LoginCodeRequest.tsx, and it is correct: for the
// length of one round trip there is nothing useful to type, and editing a
// value that is already being checked only confuses which code the answer is
// about. What disabling cannot be is the GUARD against the storm above,
// because it lifts the instant the refusal lands, and at that moment the whole
// wrong code is still sitting in the cells, still "complete" — so the
// user's very next keystroke fires the next POST. Widening it (staying
// disabled through the correction) is worse than the disease: a fast retype
// then LOSES keystrokes into the disabled inputs and mangles the code. So the
// guard is in the state machine instead, and it lives here rather than in the
// component so it can be tested.
// ----------------------------------------------------------------------

/**
 * The state the code step returns to when the server refuses `refused`.
 *
 * The cells are CLEARED, which is what makes the next keystroke harmless:
 * `isComplete('')` is false, so nothing can auto-fire until a whole new code
 * has been entered. It also makes "type the code again" the obvious next
 * action rather than "hunt for the wrong digit".
 */
export function afterRefusal(refused: string): {
  code: string;
  focus: number;
  lastRefused: string;
} {
  return { code: '', focus: 0, lastRefused: refused };
}

/**
 * May this value be submitted WITHOUT the user asking for it?
 *
 * No, if it is the value the server just refused. Clearing alone does not
 * cover re-pasting the same wrong code (from an older email, say) — that
 * refills every cell at once and would auto-fire again. An identical value
 * cannot have become valid in the meantime, so it never costs a request on its
 * own; the explicit button still submits it if the user insists.
 */
export function canAutoSubmit(code: string, lastRefused: string | null): boolean {
  return isComplete(code) && code !== lastRefused;
}

/**
 * May this value be sent to /verify-code at all, right now?
 *
 * The gate for EVERY path into a verify — auto-submit, Enter, and the explicit
 * button — so there is one place that decides, and the button's disabled state
 * is computed from the same call rather than a parallel expression.
 *
 * `sending` is the term that was missing, and it is a real race, not tidiness.
 * A resend MINTS A NEW CODE, which invalidates the one still sitting in the
 * cells (observed against GoTrue, not assumed). While `/login-code` is in
 * flight the old digits are still complete and the button still live, so a tap
 * on Sign in — or Enter, or the last keystroke of a retype — verified a code
 * that was in the act of being replaced. Depending on which request landed
 * first, the user either signed in with a credential they believed superseded,
 * or spent it and was told it was invalid with nothing to explain why.
 *
 * NOTE this is a submission guard, not a disabled attribute. Disabling the
 * CELLS for the length of a round trip would lose keystrokes on a fast retype
 * — see the storm notes above, where that is spelled out. Refusing the submit
 * costs the user nothing: a resend clears the cells on arrival anyway.
 */
export function canVerifyNow(
  value: string,
  busy: { verifying: boolean; sending: boolean },
): boolean {
  return isComplete(value) && !busy.verifying && !busy.sending;
}

/**
 * Is a verify response still the one this form is waiting for?
 *
 * `canVerifyNow` stops a SECOND request starting; this decides what to do with
 * a FIRST one that is already in flight when the user moves on. "Change email"
 * is the case that matters: it is a plain button, it can be clicked in the
 * moment between the last digit auto-submitting and the answer arriving, and
 * without this the answer is applied anyway — `setSession`, the token holder,
 * `navigate` — signing the user in as the address they just navigated away
 * from, on a form now showing a different one. Silently authenticated as the
 * wrong account is the bad kind of bug.
 *
 * Disabling the button while `verifying` is an affordance, not the fix: a
 * click that lands a millisecond before the disable does nothing about the
 * request already on the wire — the same reason the storm guard above is a
 * state-machine rule rather than a `disabled` attribute. It also strands a user
 * who wants to leave a hung request, so the button stays live and the RESULT is
 * discarded instead.
 *
 * Callers snapshot the counter before awaiting and re-check it before touching
 * any state — success and refusal alike, so a stale refusal cannot plant an
 * error or a `lastRefused` against a code the form no longer holds.
 */
export function isCurrentAttempt(startedAt: number, current: number): boolean {
  return startedAt === current;
}

/**
 * Which refusal message belongs with the value the cells now hold — the other
 * half of `canAutoSubmit`, and the thing that stops it becoming a dead end.
 *
 * Wiping the message on the first keystroke of a correction is right, but only
 * while the value is genuinely changing. Retyping the SAME code strands the
 * user: keystroke 1 clears the message, `canAutoSubmit` then suppresses the
 * POST on keystroke OTP_LENGTH (an identical value cannot have become valid),
 * and the form sits FULL, with no error, no request in flight and nothing to
 * say that **Sign in** now has to be pressed. Re-pasting the same code out of
 * the same email lands in exactly the same place.
 *
 * So the refusal comes BACK the moment the refused value is reassembled. The
 * suppression is kept — this costs no request — and the user sees why nothing
 * happened, next to a button that will still submit it if they insist.
 */
export function errorForValue(
  next: string,
  lastRefused: string | null,
  refusal: unknown,
): unknown {
  return next === lastRefused ? refusal : null;
}
